<template>
  <div class="f-h gw-checklists">
    <FiltersZone :modal-title="$t('message.OPTIONS')">
      <ComponentsList :value="gwChecklistsOptions" @edit="paramsEdit" />
    </FiltersZone>
    <div class="f-h-below-filters">
      <div v-for="team in compTeams" :key="team.id">
        <h2 class="basic-sub-title">
          {{ team.name }}
        </h2>
        <div v-if="team.gwChecklists.length === 0" class="h-v-centerer">
          {{ $t("message.GW_TEAM_NO_CHECKLISTS") }}
        </div>
        <GwEditionChecklist
          v-for="checkList in team.gwChecklists"
          :key="checkList.id"
          ref="clItem"
          :class="{ 'no-transition': demoMode }"
          :check-list="checkList"
          :disabled="team.id !== currentTeam.id"
          @delete="DELETE_GW_CHECKLIST({ ...$event, id: checkList.id })"
        />
        <div
          v-if="team.id === currentTeam.id"
          class="gw-item gw-checklists__new-checklist"
          @click="CREATE_GW_CHECKLIST"
        >
          {{ $t('message.GW_NEW_CL') }}
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/**
 * Page displaying the list of checklists.
 */

import { isEqual } from 'lodash';
import { createNamespacedHelpers } from 'vuex';
// Components
import ComponentsList from '@/components/components-list.vue';
import FiltersZone from '@/components/filters-zone.vue';
import GwEditionChecklist from '@/components/gw-edition-checklist.vue';
// Mixins
import mixinGwChecklistsOptions from '@/mixins/page-options/gw-checklists.vue';
// Vuex utils
import {
  FETCH_GW_CHECKLISTS,
  CREATE_GW_CHECKLIST,
  DELETE_GW_CHECKLIST,
} from '@/store/modules/gemba-walks/action-types';
// Utils
import IAHelp from '@/utils/classes/IAHelp';
import IAHelpStep from '@/utils/classes/IAHelpStep';

const { mapActions } = createNamespacedHelpers('gembaWalks');


export default {

  name: 'GwCheckLists',

  components: {
    ComponentsList,
    FiltersZone,
    GwEditionChecklist,
  },

  mixins: [mixinGwChecklistsOptions],

  data() {
    return {
      demoCL: [],
      pageTitle: this.$t('message.GW_CHECKLISTS'),
    };
  },

  computed: {
    compTeams() {
      if (this.demoMode) {
        return [{ ...this.currentTeam, gwChecklists: this.demoCL }];
      }
      const teamIds = this.gwChecklistsOptSelectedTeams.length > 0
        ? this.gwChecklistsOptSelectedTeams
        : Object.keys(this.$store.state.gembaWalks.checklistsPerTeam).map(id => Number(id));
      if (this.gwChecklistsOptSelectedTeams.length === 0 && !teamIds.includes(this.currentTeam.id)) {
        teamIds.push(this.currentTeam.id);
      }
      return teamIds
        .map((teamId) => {
          const team = this.allTeamsPerId[`${teamId}`];
          return { ...team, gwChecklists: this.$store.state.gembaWalks.checklistsPerTeam[`${teamId}`] || [] };
        }, [])
        .sort((team1, team2) => {
          if (team1.id === this.currentTeam.id) {
            return -1;
          }
          return team2.id === this.currentTeam.id ? 1 : 0;
        });
    },
    /** Array: Override of mixinGwChecklistsOptions to set the value for the teams picker */
    gwChecklistsOptSelectedTeams: {
      get() {
        if (this.$route.query.gwChecklistsOptSelectedTeams) {
          return this.$route.query.gwChecklistsOptSelectedTeams
            .split(',')
            .map(v => Number(v))
            .filter((teamId) => {
              const team = this.allTeamsPerId[`${teamId}`];
              return team && team.hierarchyLevel <= this.currentTeam.hierarchyLevel;
            });
        }
        return [];
      },
      set(teams) {
        teams = teams instanceof Array ? teams : [];
        const query = Object.assign({}, this.$route.query);
        query.gwChecklistsOptSelectedTeams = teams.map(v => Number(v)).join(',');
        if (!isEqual(query, this.$route.query)) {
          this.$router.replace({ query });
        }
      },
    },
    /** Object: allTeamsTree as an object in the following format: { <team_id>: <team_object>, ... }  */
    allTeamsPerId() {
      return this.$store.getters.allTeamsPerId;
    },
  },

  watch: {
    gwChecklistsOptSelectedTeams() {
      this.refresh();
    },
    currentTeam: {
      handler(newTeam, oldTeam) {
        // When switching between teams, forget query params
        if (newTeam.id !== oldTeam.id) {
          this.gwChecklistsOptSelectedTeams = [];
        }
      },
      deep: true,
    },
  },

  created() {
    this.refresh();
  },

  methods: {
    refresh() {
      const teams = this.gwChecklistsOptSelectedTeams.length ? this.gwChecklistsOptSelectedTeams : undefined;
      this.FETCH_GW_CHECKLISTS({ teams });
    },
    /** A value of the page options has been updated */
    paramsEdit(option) {
      if (this[option.key] !== undefined) {
        this[option.key] = option.value;
      }
    },
    ...mapActions({
      FETCH_GW_CHECKLISTS,
      CREATE_GW_CHECKLIST,
      DELETE_GW_CHECKLIST,
    }),
    createHelpSteps() {
      this.makeDemoValues();
      return [
        new IAHelpStep(null, this.$t('help.GW_CL_INTRO')),
        new IAHelpStep('.gw-item .editable-label', this.$t('help.GW_CL_ITEM_NAME')),
        new IAHelpStep('.gw-item .expand-area', this.$t('help.GW_CL_ITEM_EXPAND'), () => {
          if (this.$refs.clItem[0] && this.$refs.clItem[0].expand instanceof Function) {
            this.$refs.clItem[0].expand();
          }
        }),
        new IAHelpStep('.checklist-item', this.$t('help.GW_CL_ITEM_ITEMS')),
        new IAHelpStep('.checklist-item img.cl-item-delete', this.$t('help.GW_CL_ITEM_DELETE_ITEMS')),
        new IAHelpStep('.gw-item .expand-area', this.$t('help.GW_CL_ITEM_UNEXPAND')),
        new IAHelpStep('.gw-item .delete-btn', this.$t('help.GW_CL_DELETE')),
      ];
    },
    makeDemoValues() {
      this.demoCL = IAHelp.demoGWChecklists();
    },
  },
};
</script>

<style lang="scss">
.gw-checklists {
  &__new-checklist {
    margin-bottom: 1em;
  }

  .f-h-below-title {
    > *:last-child {
      margin-bottom: 7em;
    }
  }
}
</style>
